import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, ClipboardList, BarChart3, GraduationCap, Plus } from 'lucide-react';
import { authFetch } from "./api";

const API = 'https://hs.aida-advisory.com';

const Home = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ players: 0, criteria: 0, apiConnected: false, dbConnected: false });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [healthRes, playersRes, criteriaRes] = await Promise.all([
          authFetch(`${API}/health`),
          authFetch(`${API}/api/players/`),
          authFetch(`${API}/api/criteria/`)
        ]);
        const health = await healthRes.json();
        const playersData = await playersRes.json();
        const criteriaData = await criteriaRes.json();
        setStats({
          players: playersData.length || 0,
          criteria: criteriaData.length || 0,
          apiConnected: health.status === 'healthy',
          dbConnected: health.status === 'healthy'
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: Users,
      title: 'Prospect Database',
      description: 'Search and evaluate high school prospects by position, class year, and region',
      count: stats.players,
      countLabel: 'prospects in database',
      link: '/players',
      color: '#00D4FF'
    },
    {
      icon: ClipboardList,
      title: 'Scouting Criteria',
      description: 'Manage evaluation criteria including character, academics, athletic ability, and position performance',
      count: stats.criteria,
      countLabel: 'criteria in database',
      link: '/criteria',
      color: '#00E676'
    },
    {
      icon: BarChart3,
      title: 'Recruit Board',
      description: 'Prospects ranked by organizational fit score — filter by position and class year',
      link: '/recruit-board',
      color: '#FFB300'
    },
    {
      icon: GraduationCap,
      title: 'Academic Tracking',
      description: 'NCAA-required GPA and eligibility tracking for every prospect',
      link: '/academic-tracking',
      color: '#7E57C2'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      <div className="max-w-7xl mx-auto px-4 py-8 md:px-6 md:py-12">

        {/* Hero */}
        <div className="text-center mb-10 md:mb-16 fade-in">
          <img src="/SchemeSync_Logo.png" alt="SchemeSync HS" className="mx-auto mb-6 md:mb-8 h-20 md:h-32 w-auto" />
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Welcome to <span className="text-[#00D4FF]">Scheme</span><span className="text-white">Sync</span>
            <span className="text-[#00E676] ml-2 md:ml-3 text-2xl md:text-4xl">HS</span>
          </h1>
          <p className="text-base md:text-xl text-[#B0BEC5] max-w-2xl mx-auto">
            High school prospect evaluation platform — track academics, grade athleticism, and identify the right fits for your program. Powered by AiDA Advisory.
          </p>
        </div>

        {/* NCAA Notice Banner */}
        <div className="mb-6 md:mb-8 fade-in delay-100 rounded-lg border border-[#FFB300]/40 bg-[#FFB300]/10 px-4 md:px-5 py-4 flex items-start gap-3">
          <GraduationCap size={20} className="text-[#FFB300] flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-[#FFB300] font-semibold text-sm">NCAA Compliance — </span>
            <span className="text-[#B0BEC5] text-sm">GPA is tracked and displayed on every prospect profile as required by NCAA scouting guidelines. Eligibility status is updated manually by your staff.</span>
          </div>
        </div>

        {/* System Status */}
        <div className="panel-card mb-8 fade-in delay-200">
          <h2 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
            <div className="w-2 h-2 bg-[#00E676] rounded-full animate-pulse"></div>
            System Status
          </h2>
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${stats.apiConnected ? 'bg-[#00E676]' : 'bg-[#EF5350]'}`}></div>
              <span className="text-[#B0BEC5]">API: {stats.apiConnected ? 'Connected' : 'Disconnected'}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${stats.dbConnected ? 'bg-[#00E676]' : 'bg-[#EF5350]'}`}></div>
              <span className="text-[#B0BEC5]">Database: {stats.dbConnected ? 'Ready' : 'Not Ready'}</span>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          {features.map((feature, index) => (
            <Link
              key={feature.title}
              to={feature.link}
              className="panel-card md:hover:scale-[1.02] transition-transform cursor-pointer fade-in tap"
              style={{ animationDelay: `${(index + 3) * 100}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: `${feature.color}20`, border: `1px solid ${feature.color}40` }}>
                  <feature.icon className="w-7 h-7 md:w-8 md:h-8" style={{ color: feature.color }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg mb-1">{feature.title}</h3>
                  <p className="text-[#B0BEC5] text-sm mb-3">{feature.description}</p>
                  {feature.count !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="font-rajdhani font-bold text-2xl" style={{ color: feature.color }}>
                        {feature.count}
                      </span>
                      <span className="text-[#546E7A] text-sm">{feature.countLabel}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="panel-card fade-in delay-700">
          <h2 className="text-white font-semibold text-lg mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <Link to="/players" className="btn-primary text-center">Search Prospects</Link>
            <button
              onClick={() => navigate('/players', { state: { openAddPlayer: true } })}
              className="btn-secondary flex items-center justify-center gap-2">
              <Plus size={16} /> Add Prospect
            </button>
            <Link to="/criteria" className="btn-secondary text-center">Manage Criteria</Link>
            <Link to="/recruit-board" className="btn-secondary text-center">View Recruit Board</Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Home;
